import {
  checkIsValidFieldName,
  customFieldResourceTypeLabels,
  CustomFieldType,
} from "@repo/app-types/entities/customField";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { Input } from "@repo/ui/Inputs/input";
import { Textarea } from "@repo/ui/Inputs/textarea";
import type { SimpleSelectOption } from "@repo/ui/Select/simpleSelect";
import { SimpleSelect } from "@repo/ui/Select/simpleSelect";
import { type FC, useMemo, useState } from "react";
import { useCreateDataExtractionFieldDefinitionMutation } from "../../../api/entities/dataExtractionField.js";
import type { DataExtractionResourceType } from "./dataExtractionSettingsSection.js";

const fieldTypeOptions = [
  {
    label: "Yes/No",
    value: CustomFieldType.Boolean,
  },
  {
    label: "Number",
    value: CustomFieldType.Number,
  },
  {
    label: "Text",
    value: CustomFieldType.Text,
  },
];

export interface DataExtractionFieldDefinitionCreateFormProps {
  close: () => void;
  availableResourceTypes: readonly DataExtractionResourceType[];
}

export const CreateDataExtractionFieldDefinitionForm: FC<
  DataExtractionFieldDefinitionCreateFormProps
> = ({ close, availableResourceTypes }) => {
  // Build resource type options based on available types
  const resourceTypeOptions = useMemo<
    SimpleSelectOption<DataExtractionResourceType>[]
  >(
    () =>
      availableResourceTypes.map((type) => ({
        label: customFieldResourceTypeLabels[type],
        value: type,
      })),
    [availableResourceTypes],
  );

  // If only one resource type is available, default to it
  const defaultResourceType =
    availableResourceTypes.length === 1
      ? (availableResourceTypes[0] ?? null)
      : null;

  const [resourceType, setResourceType] =
    useState<DataExtractionResourceType | null>(defaultResourceType);

  const [createDataExtractionFieldDefinition, { isLoading }] =
    useCreateDataExtractionFieldDefinitionMutation();

  const [displayName, setDisplayName] = useState("");
  const [description, setDescription] = useState("");
  const [fieldName, setFieldName] = useState("");
  const [fieldType, setFieldType] = useState<CustomFieldType | null>(null);
  const [error, setError] = useState<string | null>(null);

  const fieldNameError =
    fieldName.trim() !== "" && !checkIsValidFieldName(fieldName)
      ? "Key must contain only lowercase letters, numbers, and underscores"
      : null;

  const handleSubmit = async () => {
    // This check already occurs with `isFormValid` but adding for type safety.
    if (
      !displayName.trim() ||
      !fieldType ||
      !fieldName.trim() ||
      !resourceType
    ) {
      console.error(
        "Display name, key, resource type, and field type are required",
      );
      return;
    }

    try {
      await createDataExtractionFieldDefinition({
        field_name: fieldName,
        resource_type: resourceType,
        display_name: displayName,
        field_type: fieldType,
        description: description.trim() || null,
      }).unwrap();

      close();
    } catch (error: any) {
      console.error(
        "Failed to create data extraction field definition:",
        error,
      );

      // Handle specific error for duplicate key
      if (error.status === 409) {
        setError(
          error.data?.error || "A field with a similar key already exists",
        );
      } else {
        setError("Failed to create field. Please try again.");
      }
    }
  };

  const isResourceTypeSelectorVisible = availableResourceTypes.length > 1;

  const isFormValid =
    displayName.trim() !== "" &&
    fieldType !== null &&
    fieldName.trim() !== "" &&
    resourceType !== null &&
    !fieldNameError;

  return (
    <div className="mt-4 flex w-[560px] flex-col space-y-4">
      {isResourceTypeSelectorVisible ? (
        <div className="flex flex-col gap-2">
          <SimpleSelect<DataExtractionResourceType>
            label="Resource"
            options={resourceTypeOptions}
            value={resourceType}
            onChange={setResourceType}
            placeholder="Select resource type"
          />
          <p className="text-sm text-muted-foreground">
            Specify which type of resource this data extraction field belongs
            to.
          </p>
        </div>
      ) : null}

      <Input
        label="Name"
        id="display_name"
        value={displayName}
        onChange={(e) => setDisplayName(e.target.value)}
        placeholder="Years of customer service experience"
        description="Describe the data that should be extracted. For example, 'Years of JavaScript experience' or 'Has a valid nursing license'."
      />

      <Textarea
        label="Description"
        id="description"
        value={description}
        onChange={setDescription}
        placeholder="If multiple values are mentioned in the conversation, use the most recent one."
        description="Optional. Extra context and instructions that guide the extraction and document what this field means."
        textareaClassName="min-h-12"
        maxLength={5000}
      />

      <Input
        label="Key"
        id="field_name"
        value={fieldName}
        onChange={(event) => setFieldName(event.target.value)}
        placeholder="years_of_customer_service_experience"
        description="Key of the data extraction field. This will be used in the public API. Accepted values are alphanumeric characters and underscores."
        errorDescription={fieldNameError || undefined}
        maxLength={100}
      />

      <SimpleSelect
        label="Field type"
        options={fieldTypeOptions}
        value={fieldType}
        onChange={setFieldType}
        placeholder="Select field type"
      />

      {error && <div className="text-sm text-destructive">{error}</div>}

      <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
        <div className="ml-3">
          <Button
            variant={ButtonVariant.Primary}
            onClick={handleSubmit}
            isLoading={isLoading}
            isDisabled={!isFormValid}
          >
            Create field
          </Button>
        </div>
        <Button variant={ButtonVariant.Secondary} onClick={close}>
          Cancel
        </Button>
      </div>
    </div>
  );
};
